import { type ApolloCache, gql, type NormalizedCacheObject, useApolloClient, useQuery } from '@apollo/client';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Box } from '@zillow/constellation-10';
import {
  type AddMessage_AddMessageToConversationV2Mutation,
  ConversationList_GetConversationsDocument,
  type ConversationList_GetConversationsQuery,
  type ConversationList_GetConversationsQueryVariables,
  Folder,
  useMessageList_GetConversationQuery,
} from '../../types/GraphqlTypes';
import { useQueryClient } from '@zg-rentals/react-query5';
import type { SatelliteWebLandlordGetAttachmentsMetadataResponseDto } from '@zg-rentals/ts-satellite-client';
import { useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata } from '@zg-rentals/ts-satellite-client-react-query';
import { mapAttachmentToMetadataDto } from '../../utils/attachmentMetadataUtils';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { MAX_OLD_CONVERSATION_DAYS } from '../../constants/ConfigConstants';
import { type ConversationListConversation } from '../ConversationList';
import type { ConversationIdentity } from '../MessageListItems';
import { inboxPagePreventMarkAsReadVariable, inboxPageShouldScrollToRenterProfileVariable } from '../../lib/apollo';
import { MESSAGE_TYPES } from '@zg-rentals/satellite-components';
import { InboxPageMobileView } from '../../types/InboxPageMobileView';
import useConversationActions from '../../hooks/useConversationActions';
import useDevices from '../../hooks/useDevices';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import { useAuth } from '@zg-rentals/auth-comet';
import { TRIGGER_LOCATION } from '../../constants/clickstreamEvents/globals';
import { emitRenterProfileChatBubbleAboutMeClickEvent } from '../../constants/clickstreamEvents/renterProfile';
import AddMessage from '../AddMessage';
import EmptyContainer from './components/EmptyContainer';
import MessageListItems from '../MessageListItems';
import { type AttachmentsForMessage } from '../MessageAttachments';
import { logError } from '@zg-rentals/log-error';

export default function MessageList(): JSX.Element {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();
  const {
    conversationId,
    listingAlias,
    clientTimeZone,
    mobileView,
    folder,
    daysBack,
    isByProperty,
    readRepliedStatus,
    renterProgressStatus,
  } = useContext(InboxPageContext);
  const { changeMobileView } = useInboxRouterHelper();
  const { isDesktopView, isMobileView } = useDevices();
  const { markConversationAsRead } = useConversationActions();
  const { user } = useAuth();
  const { data: inboxPageVariables } = useQuery(GET_INBOX_PAGE_VARIABLES, { fetchPolicy: 'cache-only' });

  const handleResendMessageComplete = useCallback(
    ({ conversation }: { conversation: ConversationIdentity }) => {
      const { listingAlias = '', clientTimeZone } = conversation;
      const conversationListData = apolloClient.cache.readQuery<
        ConversationList_GetConversationsQuery,
        ConversationList_GetConversationsQueryVariables
      >({
        query: ConversationList_GetConversationsDocument,
        variables: {
          listingAlias: isByProperty ? listingAlias : null,
          clientTimeZone,
          daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
          folder,
          readRepliedStatus,
          renterProgressStatus,
        },
      });
      let currentConversation: ConversationListConversation | undefined;
      const updatedConversations =
        conversationListData?.ZRM_getLatestConversationsV2.conversations.filter((conv) => {
          if (conv.conversationId === conversation.conversationId) {
            currentConversation = conv;
          }
          return conv.conversationId != conversation.conversationId;
        }) || [];

      apolloClient.cache.writeQuery({
        query: ConversationList_GetConversationsDocument,
        variables: {
          listingAlias: isByProperty ? listingAlias : null,
          clientTimeZone,
          daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
          folder,
          readRepliedStatus,
          renterProgressStatus,
          // Custom flag used in ZRM_getLatestConversationsV2 apollo cache merge function
          skipMerge: true,
        },
        data: {
          ZRM_getLatestConversationsV2: {
            ...conversationListData?.ZRM_getLatestConversationsV2,
            conversations: [currentConversation, ...updatedConversations],
          },
        },
      });
    },
    [apolloClient, daysBack, folder, isByProperty, readRepliedStatus, renterProgressStatus],
  );
  const { data: conversationData } = useMessageList_GetConversationQuery({
    variables: {
      conversationId: conversationId || '',
      listingAlias: listingAlias || '',
      clientTimeZone,
    },
    fetchPolicy: 'cache-first',
    skip: !conversationId,
  });

  const messageListContainerRef = useRef<HTMLDivElement>(null);
  const addMessageContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /**
     * As the AddMessage text area grows in height, scroll MessageList
     * to the bottom so that the latest message is always in view
     */
    let resizeObserver: ResizeObserver | null = null;
    if (window.ResizeObserver && addMessageContainerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        if (messageListContainerRef.current) {
          messageListContainerRef.current.scrollTo(0, messageListContainerRef.current.scrollHeight);
        }
      });
      resizeObserver.observe(addMessageContainerRef.current);
    }
    return () => {
      addMessageContainerRef.current && resizeObserver?.unobserve(addMessageContainerRef.current);
      resizeObserver?.disconnect();
    };
  }, [conversationId]);

  const conversation = useMemo(
    (): ConversationIdentity => ({
      conversationId: conversationId ?? '',
      listingAlias,
      clientTimeZone,
    }),
    [conversationId, listingAlias, clientTimeZone],
  );

  const handleAddMessageCompletedCacheUpdate = useCallback(
    ({
      cache,
      newMessage,
      attachmentsForMessage,
    }: {
      cache: ApolloCache<NormalizedCacheObject>;
      newMessage: Partial<AddMessage_AddMessageToConversationV2Mutation['ZRM_addMessageToConversationV2']>;
      attachmentsForMessage: AttachmentsForMessage;
    }) => {
      if (!newMessage?.messageId?.toString().includes('-') && folder) {
        try {
          const conversationListData = cache.readQuery<
            ConversationList_GetConversationsQuery,
            ConversationList_GetConversationsQueryVariables
          >({
            query: ConversationList_GetConversationsDocument,
            variables: {
              listingAlias: isByProperty ? listingAlias : null,
              clientTimeZone,
              daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
              folder,
              readRepliedStatus,
              renterProgressStatus,
            },
          });

          let currentConversation: ConversationListConversation | undefined;
          const updatedConversations =
            conversationListData?.ZRM_getLatestConversationsV2?.conversations?.filter((conversation) => {
              if (conversation.conversationId === conversationId) {
                currentConversation = conversation;
              }
              return conversation.conversationId != conversationId;
            }) || [];

          cache.writeQuery({
            query: ConversationList_GetConversationsDocument,
            variables: {
              listingAlias: isByProperty ? listingAlias : null,
              clientTimeZone,
              daysBack: daysBack ? MAX_OLD_CONVERSATION_DAYS : null,
              folder,
              readRepliedStatus,
              renterProgressStatus,
              // Custom flag used in ZRM_getLatestConversationsV2 apollo cache merge function
              skipMerge: true,
            },
            data: {
              ZRM_getLatestConversationsV2: {
                ...conversationListData?.ZRM_getLatestConversationsV2,
                conversations: currentConversation
                  ? [currentConversation, ...updatedConversations]
                  : updatedConversations,
              },
            },
          });
        } catch (error) {
          logError({
            error,
            context: {
              details: 'AddMessage - Failed to update conversation list cache after sending message',
              conversationId,
              listingAlias,
            },
          });
        }

        // Update getAttachmentsMetadata React Query cache when sending a message with attachments
        if (attachmentsForMessage.length) {
          try {
            const queryKey = useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata.getKey({
              conversationId: conversationId!,
            });
            const existingData =
              queryClient.getQueryData<SatelliteWebLandlordGetAttachmentsMetadataResponseDto>(queryKey);
            const existingDtos = existingData?.attachmentMetadataDtos ?? [];
            const newDtos = attachmentsForMessage.map(mapAttachmentToMetadataDto);
            queryClient.setQueryData<SatelliteWebLandlordGetAttachmentsMetadataResponseDto>(queryKey, {
              attachmentMetadataDtos: [...newDtos, ...existingDtos],
            });
          } catch (error) {
            logError({
              error,
              context: {
                details: 'AddMessage - Failed to update getAttachmentsMetadata cache after sending message',
                conversationId,
                listingAlias,
                attachmentsCount: attachmentsForMessage.length,
              },
            });
          }
        }
      }
    },
    [
      conversationId,
      listingAlias,
      clientTimeZone,
      daysBack,
      folder,
      isByProperty,
      queryClient,
      readRepliedStatus,
      renterProgressStatus,
    ],
  );

  if (!conversationId) {
    return <EmptyContainer />;
  }

  const inboxPageVariablesData = inboxPageVariables ?? {};
  const preventMarkAsRead = inboxPageVariablesData.inboxPagePreventMarkAsRead ?? false;
  const showEmptyContainer = inboxPageVariablesData.inboxPageShowEmptyContainer ?? false;
  const conversationById = conversationData?.ZRM_getConversationById;
  const fullMessageCountNumber = Number(conversationById?.pagination?.queryFullMessageCount) || 0;
  const hasQuickResponses = conversationById?.hasQuickResponses ?? false;
  const quickResponses = conversationById?.quickResponses ?? null;
  const isConversationBlockedByZillow = conversationById?.isConversationBlockedByZillow ?? false;
  const isPhoneLead = conversationById?.inquiry?.isPhoneLead ?? false;
  const isSpam = conversationById?.status?.isSpam ?? false;
  const showMoveToInboxBanner = folder === Folder.Spam && !isConversationBlockedByZillow;

  const handleScrollComplete = () => {
    if (preventMarkAsRead || !conversationById?.hasUnreadMessage) return;
    if (!isDesktopView && mobileView !== InboxPageMobileView.MessageList) return;
    const messages = conversationById.conversation;
    if (messages?.length) {
      markConversationAsRead(messages[messages.length - 1].messageDateMs);
    }
  };

  const handleRenterProfileClick = () => {
    if (isMobileView) {
      emitRenterProfileChatBubbleAboutMeClickEvent(user, TRIGGER_LOCATION.IreneInbox);
      changeMobileView(InboxPageMobileView.RenterProfile);
    }
  };

  const handleSystemMessageCtaClick = ({ messageType, url }: { messageType: string; url: string }) => {
    if (messageType === MESSAGE_TYPES.RENTER_PROFILE_SUBMISSION) {
      if (isMobileView) {
        changeMobileView(InboxPageMobileView.RenterProfile);
      }
      inboxPageShouldScrollToRenterProfileVariable(true);
      return;
    }
    window.open(url, '_blank');
  };

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        minHeight: 0,
      }}
    >
      <MessageListItems
        ref={messageListContainerRef}
        conversation={conversation}
        showEmptyContainer={showEmptyContainer}
        shouldRefetchAttachments={true}
        showMoveToInboxBanner={showMoveToInboxBanner}
        onResendMessageComplete={handleResendMessageComplete}
        onConversationChange={() => inboxPagePreventMarkAsReadVariable(false)}
        onRenterProfileClick={handleRenterProfileClick}
        onSystemMessageCtaClick={handleSystemMessageCtaClick}
        onScrollComplete={handleScrollComplete}
        triggerLocation={TRIGGER_LOCATION.IreneInbox}
      />
      <AddMessage
        ref={addMessageContainerRef}
        conversationId={conversationId!}
        listingAlias={listingAlias!}
        clientTimeZone={clientTimeZone}
        fullMessageCountNumber={fullMessageCountNumber}
        hasQuickResponses={hasQuickResponses}
        quickResponses={quickResponses}
        isConversationBlockedByZillow={isConversationBlockedByZillow}
        isPhoneLead={isPhoneLead}
        isSpam={isSpam}
        triggerLocation={TRIGGER_LOCATION.IreneInbox}
        onAddMessageCompletedCacheUpdate={handleAddMessageCompletedCacheUpdate}
      />
    </Box>
  );
}

const GET_INBOX_PAGE_VARIABLES = gql`
  query {
    inboxPagePreventMarkAsRead @client
    inboxPageShowEmptyContainer @client
  }
`;
